import {createSlice} from '@reduxjs/toolkit';
import {openSnackbar, setAgyleHeaderLoading, setSnackbar} from './globals';
import {
    bulkCreateAppointmentSessionObjects,
    createAppointmentSessionObject,
} from '../../pages/appointments/utils/appointmentSessionUtils';
import {
    SLOT_STATUSES,
} from '../../pages/appointments/utils/constants/appointmentSessionConstants';
import moment from 'moment';
import _, {cloneDeep, isEmpty, isString} from 'lodash';
import {setLoading} from './patient';
import {
    APPOINTMENT_MODALS, CONTEXT_OPTIONS,
    SLOT_MANIPULATION_OPTIONS,
} from '../../pages/appointments/utils/constants/appointmentSessionConstants';
import {getSlotsForSession} from '../../pages/appointments/components/utils/rruleUtils';
import API from '../../app/api';
import {getConsultantName} from '../../pages/centralPage/helper';
import APIForm from '../../app/apiForm';
import {AppointmentSlotStatus} from '../../pages/appointments/components/utils/slotUtils';
import { sendUserAudit } from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';
import { setShowDischargeTemplates } from './patientContext';
import { getConfig } from '../../util/configUtil';

export const VIEW_FILTER_STATES = {
    ALL: 'ALL',
    PTS: 'PTS',
    SLOTS: 'SLOTS',
};

export const INIT_FILTER_STATE = {
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
    locations: [],
    clinics: [],
    sessions: [],
    specialties: [],
    clinicians: [],
    statuses: [],
    practitionerRoles: [],
    view: VIEW_FILTER_STATES.ALL,
};

const INIT_MODALS_OPEN = {
    [APPOINTMENT_MODALS.ASSIGN_SESSION]: false,
    [APPOINTMENT_MODALS.ADD_EDIT_SESSION]: false,
    [APPOINTMENT_MODALS.CHANGE_REVIEW_STATUS]: false,
    [APPOINTMENT_MODALS.PATIENT_REGISTRY]: false,
    [APPOINTMENT_MODALS.STATUS_TIMELINE]: false,
};

const DAY_CALENDAR_HELPER_STATE = {
    [SLOT_MANIPULATION_OPTIONS?.LOCK_SLOT]: false,
    [SLOT_MANIPULATION_OPTIONS?.CREATE_SLOT]: false,
    [SLOT_MANIPULATION_OPTIONS?.ASSIGN]: false,
};

export const INIT_COUNTS_STATES = {
    total: 0,
    beeingSeen: 0,
    nextBeeingSeen: 0,
    notRegistered: 0,
    arrived: 0,
    departed: 0,
    doNotAttend: 0,
    notLocked: 0,
};

export const appointmentSlice = createSlice({
    name: 'appointments',
    initialState: {
        patient: null,
        view: 'patient',
        context: 'swimLanes',
        map: '',
        dayViewOpen: false,
        showWidgets: true,
        modalHelperState: null,
        modalsOpen: INIT_MODALS_OPEN,
        dayCalendarShowAdditionalTiles: false,
        dayCalendarHelperState: DAY_CALENDAR_HELPER_STATE,
        sessions: [],
        sessionsCache: {},
        appointments: [],
        appliedFilters: INIT_FILTER_STATE,
        filters: INIT_FILTER_STATE,
        daySlotsUnfiltered: [],
        daySlots: [],
        daySlotsGrouped: [],
        hiddenGroups: {},
        monthStats: {},
        filterLocations: [],
        filterClinicNames: [],
        filterSessionNames: [],
        filterSpecialties: [],
        filterClinicians: [],
        filterPractitionerRoles: [],
        currentDay: moment().startOf('day'),
        counts: INIT_COUNTS_STATES,
        listSort: null,
        isBarsContext: false,
        contextCache: {},
        practitionerRoles: [],
        facility: null,
    },
    reducers: {
        setPatient: (state, action) => {
            state.patient = action.payload;
        },
        setSelectedDate: (state, action) => {
            state.currentDay = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        setAppliedFilters: (state, action) => {
            state.appliedFilters = action.payload;
        },
        setMap: (state, map) => {
            state.map = map.payload;
        },
        setDayViewOpen: (state, open) => {
            // state.dayViewOpen = open.payload;
            state.context = 'swimLanes';
        },
        setView: (state, view) => {
            state.view = view.payload;
        },
        setContext: (state, view) => {
            state.context = view.payload;
        },
        setModalOpen: (state, modal) => {
            state.modalsOpen = {
                ...state.modalsOpen,
                ...modal.payload,
            };
        },
        setModalHelperState: (state, helperState) => {
            state.modalHelperState = helperState.payload;
        },
        setDayCalendarShowAdditionalTiles: (state, show) => {
            state.dayCalendarShowAdditionalTiles = show.payload;
        },
        setDayCalendarHelperState: (state, show) => {
            state.dayCalendarHelperState = {
                ...state.dayCalendarHelperState,
                ...show.payload,
            };
        },
        setShowWidgets: (state, modal) => {
            state.showWidgets = modal.payload;
        },
        setSessions: (state, action) => {
            state.sessions = action.payload;
        },
        setDaySlots: (state, action) => {
            state.daySlots = action.payload;
        },
        setDaySlotsGrouped: (state, action) => {
            state.daySlotsGrouped = action.payload;
        },
        setHiddenGroups: (state, action) => {
            state.hiddenGroups = action.payload;
        },
        setDaySlotsUnfiltered: (state, action) => {
            state.daySlotsUnfiltered = action.payload;
        },
        setFilterLocations: (state, action) => {
            state.filterLocations = action.payload;
        },
        setFilterClinicNames: (state, action) => {
            state.filterClinicNames = action.payload;
        },
        setFilterSessionNames: (state, action) => {
            state.filterSessionNames = action.payload;
        },
        setFilterSpecialties: (state, action) => {
            state.filterSpecialties = action.payload;
        },
        setFilterClinicians: (state, action) => {
            state.filterClinicians = action.payload;
        },
        setFilterPractitionerRoles: (state, action) => {
            state.filterPractitionerRoles = action.payload;
        },
        setCounts: (state, action) => {
            state.counts = action.payload;
        },
        setAppointments: (state, action) => {
            state.appointments = action.payload;
        },
        setMonthStats: (state, action) => {
            state.monthStats = action.payload;
        },
        setSessionsCache: (state, action) => {
            state.sessionsCache = action.payload;
        },
        setListSort: (state, action) => {
            state.listSort = action.payload;
        },
        setBarsContext: (state, action) => {
            state.isBarsContext = action.payload;
        },
        setContextCache:(state, action) => {
            state.contextCache = action.payload;
        },
        setPractitionerRoles:(state, action) => {
            state.practitionerRoles = action.payload;
        },
        setFacility:(state, action) => {
            state.facility = action.payload;
        },
    },
});

export const appointmentsDaySlotsGrouped = (state) => state.appointments.daySlotsGrouped;
export const map = (state) => state.appointments.map;
export const isWidgetsVisibleState = (state) => state.appointments.showWidgets;
export const isBarContextState = (state) => state.appointments.isBarsContext;
export const widgetCountsState = (state) => state.appointments.counts;
export const dayAppointmentsState = (state) => state.appointments.appointments;
export const sessionsState = (state) => state.appointments.sessions;
export const monthStatsState = (state) => state.appointments.monthStats;
export const activeView = (state) => state.appointments.view;
export const selectedDate = (state) => state.appointments.currentDay;
export const selectedFilters = (state) => state.appointments.filters;
export const practitionerRolesDict = (state) => state.appointments.practitionerRoles;

export const activeContext = (state) => state.appointments.context;
export const listSortState = (state) => state.appointments.listSort;
export const filterLocationsState = (state) => state.appointments.filterLocations;
export const filterClinicNamesState = (state) => state.appointments.filterClinicNames;
export const filterSessionNamesState = (state) => state.appointments.filterSessionNames;
export const filterSpecialtiesState = (state) => state.appointments.filterSpecialties;
export const filterCliniciansState = (state) => state.appointments.filterClinicians;
export const filterPractitionerRoles = (state) => state.appointments.filterPractitionerRoles;

export const selectedPatient = (state) =>
    state.appointments.modalHelperState?.patient;
export const swimLanesViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.SWIM_LANES;
export const calendarViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.CALENDAR;
export const listViewOpen = (state) => state.appointments.context === CONTEXT_OPTIONS.LIST;
// Modals
export const isAssignSessionModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.ASSIGN_SESSION];
export const isAddNewEdSessionModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.ADD_EDIT_SESSION];
export const isChangeReviewStatusModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.CHANGE_REVIEW_STATUS];
export const isPatientRegistryModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.PATIENT_REGISTRY];
export const isStatusTimelineModalOpen = (state) =>
    state.appointments.modalsOpen?.[APPOINTMENT_MODALS.STATUS_TIMELINE];
export const appointmentsModalHelperState = (state) =>
    state.appointments.modalHelperState;
export const dayCalendarShowAdditionalTiles = (state) =>
    state.appointments.dayCalendarShowAdditionalTiles;
export const dayCalendarHelperState = (state) =>
    state.appointments.dayCalendarHelperState;
export const schedulerFacility = (state) => state.appointments.facility;

export const {
    setPatients,
    setMap,
    setSelectedDate,
    setView,
    setContext,
    setModalOpen,
    setDayViewOpen,
    setModalHelperState,
    setDayCalendarShowAdditionalTiles,
    setDayCalendarHelperState,
    setShowWidgets,
    setSessions,
    setSlots,
    setDaySlots,
    setDaySlotsGrouped,
    setHiddenGroups,
    setDaySlotsUnfiltered,
    setAppliedFilters,
    setFilters,
    setFilterLocations,
    setFilterClinicNames,
    setFilterSessionNames,
    setFilterSpecialties,
    setFilterClinicians,
    setCounts,
    setAppointments,
    setMonthStats,
    setSessionsCache,
    setListSort,
    setBarsContext,
    setContextCache,
    setPractitionerRoles,
    setFilterPractitionerRoles,
    setFacility,
} = appointmentSlice.actions;

export const openAppointmentModal =
    (modal, helperState = null) =>
        (dispatch, getState) => {
            const currentStateClone = cloneDeep(getState().appointments.modalsOpen);
            currentStateClone[modal] = true;

            if (helperState) {
                dispatch(setModalHelperState(helperState));
            }

            dispatch(setModalOpen({...currentStateClone}));
        };

export const closeAppointmentModal = (modal) => (dispatch) => {
    dispatch(setModalOpen({[modal]: false}));
    dispatch(setModalHelperState(null));
};

export const changeContext = (context) => (dispatch,getState) => {
    dispatch(setContext(context));
    const a = getState().appointments;
    dispatch(updateFiltersAvailability(context,a.filterLocations,
        a.filterClinicNames,a.filterSessionNames,a.filterSpecialties,
        a.filterClinicians,a.filterPractitionerRoles,a.currentDay));
};

export const toggleBarsContext = () => (dispatch, getState) => {
    const currentContext = getState().appointments.isBarsContext;
    const state = getState().appointments;
    const newCache = {
        isBarsContext : currentContext,
        sessions : state.sessions,
        sessionsCache : state.sessionsCache,
        appointments: state.appointments,
        appliedFilters : state.appliedFilters,
        filters: state.filters,
        daySlotsUnfiltered: state.daySlotsUnfiltered,
        daySlots:state.daySlots,
        daySlotsGrouped: state.daySlotsGrouped,
        hiddenGroups: state.hiddenGroups,
        monthStats: state.monthStats,
        filterLocations: state.filterLocations,
        filterClinicNames: state.filterClinicNames,
        filterSessionNames: state.filterSessionNames,
        filterSpecialties: state.filterSpecialties,
        filterClinicians: state.filterClinicians,
        filterPractitionerRoles: state.filterPractitionerRoles,
        counts: state.counts,
        listSort: state.listSort,
    }
    if(state.contextCache.isBarsContext === (!currentContext)) {
        dispatch(setSessions(state.contextCache.sessions));
        dispatch(setSessionsCache(state.contextCache.sessionsCache));
        dispatch(setAppointments(state.contextCache.appointments));
        dispatch(setAppliedFilters(state.contextCache.appliedFilters));
        dispatch(setFilters(state.contextCache.filters));
        dispatch(setDaySlotsUnfiltered(state.contextCache.daySlotsUnfiltered));
        dispatch(setDaySlots(state.contextCache.daySlots));
        dispatch(setDaySlotsGrouped(state.contextCache.daySlotsGrouped));
        dispatch(setHiddenGroups(state.contextCache.hiddenGroups));
        dispatch(setMonthStats(state.contextCache.monthStats));
        dispatch(setFilterLocations(state.contextCache.filterLocations));
        dispatch(setFilterClinicNames(state.contextCache.filterClinicNames));
        dispatch(setFilterSessionNames(state.contextCache.filterSessionNames));
        dispatch(setFilterSpecialties(state.contextCache.filterSpecialties));
        dispatch(setFilterClinicians(state.contextCache.filterClinicians));
        dispatch(setFilterPractitionerRoles(state.contextCache.filterPractitionerRoles));
        dispatch(setCounts(state.contextCache.counts));
        dispatch(setListSort(state.contextCache.listSort));
    }else {
        dispatch(setSessions([]));
        dispatch(setSessionsCache({}));
        dispatch(setAppointments([]));
        dispatch(setAppliedFilters(INIT_FILTER_STATE));
        dispatch(setFilters(INIT_FILTER_STATE));
        dispatch(setDaySlotsUnfiltered([]));
        dispatch(setDaySlots([]));
        dispatch(setDaySlotsGrouped([]));
        dispatch(setHiddenGroups({}));
        dispatch(setMonthStats({}));
        dispatch(setFilterLocations([]));
        dispatch(setFilterClinicNames([]));
        dispatch(setFilterSessionNames([]));
        dispatch(setFilterSpecialties([]));
        dispatch(setFilterClinicians([]));
        dispatch(setFilterPractitionerRoles([]));
        dispatch(setCounts(INIT_COUNTS_STATES));
        dispatch(setListSort(null));
    }
    dispatch(setContextCache(newCache));
    dispatch(setBarsContext(!currentContext));

};

export const resetAppointments = () => (dispatch) => {
    dispatch(setSessions([]));
    dispatch(setSessionsCache({}));
    dispatch(setAppointments([]));
    dispatch(setAppliedFilters(INIT_FILTER_STATE));
    dispatch(setFilters(INIT_FILTER_STATE));
    dispatch(setDaySlotsUnfiltered([]));
    dispatch(setDaySlots([]));
    dispatch(setDaySlotsGrouped([]));
    dispatch(setHiddenGroups({}));
    dispatch(setMonthStats({}));
    dispatch(setFilterLocations([]));
    dispatch(setFilterClinicNames([]));
    dispatch(setFilterSessionNames([]));
    dispatch(setFilterSpecialties([]));
    dispatch(setFilterClinicians([]));
    dispatch(setFilterPractitionerRoles([]));
    dispatch(setCounts(INIT_COUNTS_STATES));
    dispatch(setListSort(null));
}

export const addPatientSchedulerNote = ({ptId, newValues, oldValues, patientsInWardsRef, isDeleted, noteDate}) => async (dispatch, getState) => {
    let note = '## *Appointment Schedule*\n\n';
    const session = _.find(getState().appointments.sessions, s => s.sessionId === newValues?.assignedSession);

    try {
        let snackbarMsg = "";
        const oldStatus = _.find(SLOT_STATUSES, s => s.value === oldValues?.status);
        const newStatus = _.find(SLOT_STATUSES, s => s.value === newValues?.status);
        const appointmentDateString = moment(oldValues?.startDate).format(
            "Do MMM h:mma",
        );

        if (!!isDeleted) {
            note += `* Appointment on ${appointmentDateString} has been cancelled\n`;
            snackbarMsg = "Appointment's cancellation note successfully saved. "
        } else {

            if (!oldValues && !!newValues) {
                note += `* **Appointment date**: ${moment(newValues?.startDate).format("DD/MM/YYYY hh:mm A")}\n`;

                if (newValues?.description) note += `* **Appointment reason**: ${newValues.description}\n`;
                if (session?.sessionName) note += `* **Session name**: ${session?.sessionName}\n`;
                if (session?.hospital?.description) note += `* **Hospital**: ${session?.hospital?.description}\n`;
                if (session?.mapWard?.name) note += `* **Ward name**: ${session?.mapWard?.name}\n`;
                if (session?.clinic?.description) note += `* **Clinic**: ${session?.clinic?.description}\n`;
                if (session?.specialty?.__description) note += `* **Specialty**: ${session?.specialty?.__description}\n`;

                snackbarMsg = "New scheduled appointment note successfully saved."
            } else if (newValues && oldValues){
                const date = noteDate ? moment(noteDate) : moment();

                note += `Appointment status updated from ${oldStatus?.name} to ${newStatus?.name} at ${moment(date).format("DD/MM/YYYY hh:mm A")}`;
                snackbarMsg = "New update appointment note successfully saved.";
            }
        }

        if (newValues?.__id || oldValues?.__id) {
            note += `\n <!--${newValues?.__id || oldValues?.__id || ""}-->`;
        }

        let noteObj = {
            note,
            patientsInWardsRef: [],
            noteType: 'Appointments',
        }

        if (patientsInWardsRef) noteObj.patientsInWardsRef = _.isArray(patientsInWardsRef) && !_.isEmpty(patientsInWardsRef) ?
            patientsInWardsRef : isString(patientsInWardsRef) ? [patientsInWardsRef] : [];

        const response = await API.addClinicalNote(ptId, noteObj);

        if (response) {
            dispatch(setSnackbar({type: 'success', message: snackbarMsg || 'New appointment note saved.'}));
        }
    } catch (e) {
        console.error(e);
        dispatch(setSnackbar({type: 'error', message: 'New appointment note not saved.'}));
    }
}

export const changeFilterValue = (value, filterKey) => (dispatch, getState) => {
    const newFilter = {
        ...getState().appointments.filters,
        [filterKey]: value,
    };

    dispatch(setFilters(newFilter));
};

export const changeSelectedDate = (date) => (dispatch, getState) => {
    if (!getState().appointments.currentDay.isSame(date)) {
        if (getState().appointments.filters.startDate.isAfter(date) || getState().appointments.filters.endDate.isBefore(date)) {
            let startDate = date.clone().startOf('month');
            let endDate = date.clone().endOf('month');
            dispatch(setFilters({
                ...getState().appointments.filters,
                startDate: startDate,
                endDate: endDate,
            }));
            dispatch(setSelectedDate(date));
        } else {
            dispatch(setSelectedDate(date));
        }
    }
};

export const selectPatientForAppointment =
    (patient, fetchPatientInfo = false) =>
        async (dispatch, getState) => {
            if (!patient?.__id) {
                return dispatch(
                    setSnackbar({
                        type: 'error',
                        message:
                            'Patient is not valid. Please try again later or contact support.',
                    }),
                );
            }

            try {
                let pt = patient;

                dispatch(setLoading(true));

                if (!!fetchPatientInfo) {
                    const apiResponse = await API.getPatientAttendancesHistory(pt?.__id);

                    if (!isEmpty(apiResponse)) {
                        pt = apiResponse?.[0];
                    } else {
                        dispatch(
                            setSnackbar({
                                type: 'error',
                                message:
                                    'Cannot get patient history. Please admit patient first.',
                            }),
                        );
                    }
                }

                const currentModalHelperState = getState().appointments.modalHelperState;

                dispatch(
                    setModalHelperState({
                        ...currentModalHelperState,
                        selectedPatient: pt,
                    }),
                );
            } catch (e) {
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Error selecting patient. Please try again later.',
                    }),
                );
            } finally {
                dispatch(setLoading(false));
            }
        };

//////////////////////////
// APPOINTMENT SESSIONS //
//////////////////////////

export const createAppointmentSession =
    (session, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        const newSession = createAppointmentSessionObject(session);

        try {
            const apiResponse = await API.createAppointmentSession(newSession);

            if (apiResponse) {
                if (onSuccess) {
                    onSuccess(apiResponse);
                }

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const bulkCreateAppointmentSessions =
    (sessionObj, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        const newSessions = bulkCreateAppointmentSessionObjects(sessionObj);
        try {
            const apiResponse = await API.bulkCreateAppointmentSessions({sessionList: newSessions});

            if (apiResponse) {
                onSuccess(apiResponse);

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const updateAppointmentSession =
    (sessionId, updated, onSuccess) => async (dispatch, getState) => {
        dispatch(setAgyleHeaderLoading(true));
        try {
            const apiResponse = await API.updateAppointmentSession(
                sessionId,
                updated,
            );

            if (apiResponse) {
                dispatch(
                    openSnackbar('success', 'Appointment session has been successfully updated.'),
                );
                if (onSuccess) {
                    onSuccess();
                }

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const deleteAppointmentSession =
    (sessionId, onSuccess) => async (dispatch, getState) => {
        if (!sessionId) return dispatch(
            openSnackbar('error', 'Please provide valid session.'),
        );

        dispatch(setAgyleHeaderLoading(true));
        try {
            const apiResponse = await API.deleteAppointmentSession(sessionId);

            if (apiResponse) {
                dispatch(openSnackbar('success', 'Appointment session has been successfully closed.'));
                if (onSuccess) onSuccess();

                dispatch(getAppointmentSlots(true));
            }

            return apiResponse?.__data || [];
        } catch (e) {
            console.log("ERR:", e);
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(getAppointmentSlots());
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const showExceptionSnackbar = (e) => (dispatch) => {
    const message = String(e.response?.data || e.message).split('Exception: ')?.[1];
    const msg = message.split("\n")[0];

    dispatch(
        setSnackbar({
            type: 'error',
            message: msg,
        }),
    );
};


const getSessions = (month, forceRefresh = false, hospitalId) => async (dispatch, getState) => {
    const cache = getState().appointments.sessionsCache;
    const monthStart = month.clone().startOf('month');
    const monthEnd = monthStart.clone().add(1, 'month');
    const monthStr = month.format('YYYY_MM');
    let sessionres = [];
    if (cache[monthStr] && cache[monthStr].cached > (moment().valueOf() - 600000) && !forceRefresh && cache[monthStr]?.facility === hospitalId) {
        return cache[monthStr];
    } else {
        const apiSessions = await API.getAppointmentSessions(monthStart, monthEnd,getState().appointments.isBarsContext, hospitalId);
        const apiPractitionerRoles = await APIForm.getDictForm("roles.all", "");
        if (_.isArray(apiPractitionerRoles)) dispatch(setPractitionerRoles(apiPractitionerRoles));

        _.forEach(apiSessions?.__data || [], (sess) => {
            sess.monthDate = monthStart.clone();
            getSlotsForSession(sess, monthStart, monthEnd);
            sessionres.push(sess);
        });
        const result = {
            sessions: sessionres,
            cached: moment().valueOf(),
            sessionDicts: getSessionDicts(sessionres, apiPractitionerRoles),
            facility: hospitalId,
        };
        dispatch(setSessionsCache({
            ...cache,
            [monthStr]: result,
        }));
        return result;
    }
};

const groupFilters = (sessions, keyPath, getId, getDescription, getValue, fallback) => {
    return _(sessions)
        .groupBy(sess => {
            const val = _.get(sess, keyPath);
            return val ? getId(val) : 'noVal';
        })
        .map((grouped, id) => {
            const first = grouped[0];
            const val = _.get(first, keyPath);
            const isFallback = id === 'noVal';

            return {
                __id: isFallback ? fallback.__id : getId(val),
                __description: isFallback ? fallback.__description : getDescription(val),
                value: isFallback ? fallback.value : getValue(val),
                disabled: false,
                sess: grouped
            };
        })
        .value();
};

const getSessionDicts = (sessions, practitionerRoles) => {
    let result = {locations: [], clinics: [], uniqueSessions: [], specialties: [], clinicians: [], practitionerRoles: []};
    const uniqueSessionsMapped = _.map(sessions, sess => {
        return {__id: sess?.groupId, __description: sess?.sessionName, value: sess?.groupId,sess: sess, disabled : false};
    });
    result.uniqueSessions = _.uniqBy(uniqueSessionsMapped, '__id');

    result.locations = groupFilters(
        sessions,
        'mapWard',
        ward => ward.code,
        ward => ward.name,
        ward => ward.code,
        { __id: 'noVal', __description: 'ED Department', value: 'ED' }
    );

    result.clinics = groupFilters(
        sessions,
        'clinic',
        clinic => clinic?.__id,
        clinic => clinic?.description || clinic?.__description,
        clinic => clinic?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: '' }
    );

    result.specialties = groupFilters(
        sessions,
        'specialty',
        spec => spec?.__id,
        spec => spec?.__description,
        spec => spec?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: null }
    );

    result.clinicians = groupFilters(
        sessions,
        'consultant',
        consultant => consultant?.__id,
        consultant => getConsultantName(consultant),
        consultant => consultant?.__id,
        { __id: 'noVal', __description: 'Not assigned', value: null }
    );

    result.practitionerRoles = groupFilters(
        sessions,
        'practitionerRole',
        id => id,
        id => {
            const found = _.find(practitionerRoles, { _id: id });
            return found?.name || 'Not assigned';
        },
        id => id,
        { __id: 'noVal', __description: 'Not assigned', value: null }
    );

    return result;
};

const updateFiltersAvailability =  (context,filterLocations,filterClinicNames,filterSessionNames,filterSpecialties,filterClinicians,filterPractitionerRoles,currDay) => (dispatch,getState) => {
    const sday = currDay.format('YYYY_MM_DD');
    const setDisabledFunc = (context === CONTEXT_OPTIONS.CALENDAR) ?
        (f) => {
            f.disabled = false;
        }
        :
        (f) => {
            if (_.isArray(f.sess)) {
                const anyEnabled = f.sess.some(sessItem =>
                    sessItem?.daySlots?.[sday] && !_.isEmpty(sessItem.daySlots[sday])
                );
                f.disabled = !anyEnabled;
            } else {
                f.disabled = !(f?.sess?.daySlots?.[sday] && !_.isEmpty(f?.sess?.daySlots?.[sday]));
            }
        }
    dispatch(setFilterLocations(_.forEach(_.cloneDeep(filterLocations),(f) => setDisabledFunc(f))));
    dispatch(setFilterClinicNames(_.forEach(_.cloneDeep(filterClinicNames),(f) => setDisabledFunc(f))));
    dispatch(setFilterSessionNames(_.forEach(_.cloneDeep(filterSessionNames),(f) => setDisabledFunc(f))));
    dispatch(setFilterSpecialties(_.forEach(_.cloneDeep(filterSpecialties),(f) => setDisabledFunc(f))));
    dispatch(setFilterClinicians(_.forEach(_.cloneDeep(filterClinicians),(f) => setDisabledFunc(f))));
    dispatch(setFilterPractitionerRoles(_.forEach(_.cloneDeep(filterPractitionerRoles),(f) => setDisabledFunc(f))));
}

const updateSlots = (appointments, patients, startDay) => {
    appointments.sessionshash = {};
    appointments.counts = {total: patients.length};
    appointments.counts.beeingSeen = _.countBy(patients, pt => pt.status === SLOT_STATUSES.BEEING_SEEN.value).true || 0;
    appointments.counts.notRegistered = _.countBy(patients, pt => pt.status === SLOT_STATUSES.NOT_REGISTERED.value).true || 0;
    appointments.counts.arrived = _.countBy(patients, pt => pt.status === SLOT_STATUSES.ARRIVED.value).true || 0;
    appointments.counts.doNotAttend = _.countBy(patients, pt => pt.status === SLOT_STATUSES.DNA.value).true || 0;
    appointments.counts.departed = _.countBy(patients, pt => pt.status === SLOT_STATUSES.DEPARTED.value).true || 0;
    appointments.counts.notLocked = _.countBy(patients, pt => pt.status !== 'locked').true || 0;

    // let groupedBySess = _.groupBy(patients, 'assignedSession');
    _.forEach(patients, pts => {
        pts.startDate = moment(pts.startDate);
    });
    appointments.counts.nextBeeingSeen = _.countBy(patients, pt => {
        return pt.status === SLOT_STATUSES.ARRIVED.value ||
            (pt.status === SLOT_STATUSES.NOT_REGISTERED.value && pt.startDate.isAfter(moment()));
    }).true || 0;
    let slots = [];
    let dayField = startDay.format('YYYY_MM_DD');
    _.forEach(appointments.sessions, (sess) => {
        if (sess.daySlots[dayField]) {
            slots.push(...sess.daySlots[dayField]);
        }
    });
    slots = _.unionWith(patients, slots, (arrVal, othVal) => {
        return arrVal.startDate.isSame(othVal.startDate) && arrVal.assignedSession === othVal.assignedSession;
    });
    slots = _.sortBy(slots, obj => {
        return obj.startDate.valueOf();
    });
    appointments.daySlotsUnfiltered = slots;
};

export const filterSessions = (filter, allsessions) => {
    let fsessions = _.map(allsessions, session => {
        return session;
    });
    if (filter.locations && filter.locations.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.locations, l => {
                // return l.__id === s.wardId;
                return (l.__id !== 'noVal' ? l.__id : l.value)  === s.wardId;
            });
        });
    }
    if (filter.clinics && filter.clinics.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.clinics, l => {
                return (l.value ? l.__id : l.value) === s.clinicsId;
                // return l.__id === s.clinicsId;
            });
        });
    }
    if (filter.sessions && filter.sessions.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.sessions, l => {
                return l.__id === s.groupId;
            });
        });
    }
    if (filter.specialties && filter.specialties.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.specialties, l => {
                return (l.value ? l.__id : l.value) === s.assignedSpecialty;
            });
        });
    }
    if (filter.clinicians && filter.clinicians.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.clinicians, l => {
                return (l.value ? l.__id : l.value)  === s.practitioner;
            });
        });
    }
    if (filter.practitionerRoles && filter.practitionerRoles.length > 0) {
        fsessions = _.filter(fsessions, s => {
            return _.find(filter.practitionerRoles, l => {
                return (l.value ? l.__id : l.value)  === s.practitionerRole;
            });
        });
    }
    return fsessions;
};

export const hideShowGroup = (group) => async (dispatch, getState) => {
    if(getState().appointments.hiddenGroups[group]) {
        let newHG = {...getState().appointments.hiddenGroups};
        delete newHG[group];
        await dispatch(setHiddenGroups(newHG));
    }else {
        const newHG = {...getState().appointments.hiddenGroups,[group] : true};
        await dispatch(setHiddenGroups(newHG));
    }
    dispatch(filterAppointmentSlots())
}

const calculateGrouping = (filter,filtered,fsessions,hiddenGroups,sort) => {
    if (filter && (filter.clinics?.length > 1 ||
        filter.sessions?.length > 1 ||
        filter.specialties?.length > 1 ||
        filter.clinicians?.length > 1 ||
        filter.practitionerRoles?.length > 1
    )) {
        let group = filter.clinics?.length > 1 ? {v:'clinicsId',f:filter.clinics} :
                filter.sessions?.length > 1 ? {v:'groupId',f:filter.sessions} :
                filter.specialties?.length > 1 ? {v:'assignedSpecialty',f:filter.specialties} :
                    filter.practitionerRoles?.length > 1 ? {v:'practitionerRole',f:filter.practitionerRoles} :
                    {v:'practitioner',f:filter.clinicians};
        group.ff = _.reduce(group.f , (obj,param) => {
                obj[param.__id] = param
                return obj;        }, {});
        const groupedSessions = {};
        _.forEach(fsessions, sess => {
            groupedSessions[sess.sessionId] = sess;
        });
        let grouped = _.groupBy(filtered, app => groupedSessions[app.assignedSession][group.v]);
        let result = [];
        for (var groupId in grouped) {
            var appList = grouped[groupId];
            if(appList && appList.length>0) {
                const  groupSession = groupedSessions[appList[0].assignedSession];
                result.push({
                    assignedSession : groupSession.sessionId,
                    groupName : group.ff[groupSession[group.v]].__description,
                    groupId : groupSession[group.v],
                    count:appList.length,
                    appointments: appList,
                    hidden: hiddenGroups[groupSession[group.v]]
                });
                if(!hiddenGroups[groupSession[group.v]]) {
                    if(sort) {
                        result.push(..._.orderBy(appList,sort.value,sort.direction));
                    } else {
                        result.push(...appList);
                    }
                }
            }
        }
        return result;
    }
    if(sort) {
        return _.orderBy(filtered,sort.value,sort.direction);
    }
    return filtered;
};

export const filterAppointmentSlots = () => async (dispatch, getState) => {
    let filter = getState().appointments.filters;
    let filtered = getState().appointments.daySlotsUnfiltered;
    let allsessions = getState().appointments.sessions;
    let hiddenGroups = getState().appointments.hiddenGroups;
    let sorts = getState().appointments.listSort;
    let fsessions = filterSessions(filter, allsessions);
    if (filter.view !== VIEW_FILTER_STATES.ALL) {
        let pts = filter.view === VIEW_FILTER_STATES.PTS;
        filtered = _.filter(filtered, app => {
            return pts ? (app.patient !== undefined && app.patient !== null):
                (app.patient === undefined || app.patient === null);
        });
    }
    let fsessionids = _.map(fsessions, session => {
        return session.sessionId;
    });
    if (fsessionids.length != allsessions.length) {
        filtered = _.filter(filtered, a => {
            return _.includes(fsessionids, a.assignedSession);
        });
    }
    if (filter.statuses && filter.statuses.length > 0) {
        filtered = _.filter(filtered, a => {
            return _.some(filter.statuses, s => {
                return s.__id === a.status;
            });
        });
    }
    dispatch(calculateMonthStats(filter.startDate.clone(), allsessions));
    dispatch(setDaySlots(filtered));
    dispatch(setDaySlotsGrouped(calculateGrouping(filter,filtered,fsessions,hiddenGroups,sorts)));
};

export const filterStats = (stat, filters) => {
    if (!stat || !stat.monthStart) return {};
    let fsessions = filterSessions(filters, stat.sessions);
    let monthStats = {};
    let monthStart = stat.monthStart.clone();
    let monthEnd = stat.monthEnd;
    while (monthStart.isBefore(monthEnd) || monthStart.isSame(monthEnd)) {
        let strDate = monthStart.format('YYYY_MM_DD');
        let numDay = monthStart.date();
        monthStats[numDay] = {allslots: 0, takenslots: 0};
        _.forEach(fsessions, s => {
            if (s.daySlots[strDate]) {
                monthStats[numDay].allslots += s.daySlots[strDate].length;
            }
        });
        monthStart = monthStart.add(1, 'day');
    }
    let filteredStatList = _.filter(stat.statList, s => {
        return _.findIndex(fsessions, fs => {
            return fs.sessionId == s.assignedSession;
        }) > -1;
    });
    _.forEach(filteredStatList, stat => {
        monthStats[moment(stat.appDay).date()].takenslots += stat.count;
    });
    return monthStats;
};

export const getSessionForSelecteDay = (sessions, selectedDay) => {
    const day = selectedDay.format('YYYY_MM_DD');
    return _.filter(sessions, session => {
        return session.daySlots[day];
    });
};

export const calculateMonthStats = (slelectedMonth, _allSessions) => async (dispatch, getState) => {
    const hospitalId = getState().appointments.facility;
    const apiStatResponse = await API.getMonthStats(slelectedMonth, [], [],getState().appointments.isBarsContext, hospitalId);
    let monthStats = {};
    let monthStart = slelectedMonth.clone();
    let monthEnd = monthStart.clone().endOf('month');
    const _cachedSessions = await dispatch(getSessions(monthStart, false, hospitalId));
    let allSessions = _cachedSessions.sessions;
    while (monthStart.isBefore(monthEnd) || monthStart.isSame(monthEnd)) {
        let strDate = monthStart.format('YYYY_MM_DD');
        let numDay = monthStart.date();
        monthStats[numDay] = {allslots: 0, takenslots: 0};
        _.forEach(allSessions, s => {
            if (s.daySlots[strDate]) {
                monthStats[numDay].allslots += s.daySlots[strDate].length;
            }
        });
        monthStart = monthStart.add(1, 'day');
    }
    _.forEach(apiStatResponse, stat => {
        monthStats[moment(stat.appDay).date()].takenslots += stat.count;
    });
    let monthsStats = {
        ...getState().appointments.monthStats,
        [slelectedMonth.format('YYYY_MM')]: {
            monthStats: monthStats,
            cached: moment().valueOf(),
            statList: apiStatResponse,
            sessions: allSessions,
            monthStart: slelectedMonth.clone(),
            monthEnd: monthEnd,
            sessionDicts: _cachedSessions.sessionDicts,
        },
    };
    dispatch(setMonthStats(monthsStats));

};

export const updateMonthStatCached = (slelectedMonth) => async (dispatch, getState) => {
    if (getState().appointments.monthStats[slelectedMonth.format('YYYY_MM')]) {
        if (getState().appointments.monthStats[slelectedMonth.format('YYYY_MM')].cached > (moment().valueOf() - 60000)) {
            return;
        }
    }
    dispatch(calculateMonthStats(slelectedMonth));
};

export const getAppointmentSlots = (forceRefresh = false) => async (dispatch, getState) => {
    dispatch(setAgyleHeaderLoading(true));
    let appointmentsState = getState().appointments;
    const hospId = getState().appointments.facility;

    const cachedSessions = await dispatch(getSessions(appointmentsState.filters.startDate, forceRefresh, hospId));
    dispatch(updateFiltersAvailability(appointmentsState.context,cachedSessions.sessionDicts.locations,
        cachedSessions.sessionDicts.clinics,cachedSessions.sessionDicts.uniqueSessions,cachedSessions.sessionDicts.specialties,
        cachedSessions.sessionDicts.clinicians,cachedSessions.sessionDicts.practitionerRoles,appointmentsState.currentDay));
    // dispatch(setFilterLocations(cachedSessions.sessionDicts.locations));
    // dispatch(setFilterClinicNames(cachedSessions.sessionDicts.clinics));
    // dispatch(setFilterSpecialties(cachedSessions.sessionDicts.specialties));
    // dispatch(setFilterClinicians(cachedSessions.sessionDicts.clinicians));
    // dispatch(setFilterSessionNames(cachedSessions.sessionDicts.uniqueSessions));
    try {
        let endDate = getState().appointments.currentDay.clone().add(1, 'day');
        const apiResponse = await API.getAppointmentSlots(getState().appointments.currentDay, endDate,getState().appointments.isBarsContext, hospId ? `hospitalId=${hospId}`: null);
        let appointments = {sessions: cachedSessions.sessions || [], slots: appointmentsState.slots || []};
        updateSlots(appointments, apiResponse, appointmentsState.currentDay);
        dispatch(setDaySlotsUnfiltered(appointments.daySlotsUnfiltered));
        dispatch(setCounts(appointments.counts));
        dispatch(setSessions(cachedSessions.sessions));
        dispatch(setAppointments(apiResponse));
        dispatch(setAppliedFilters({
            ...getState().appliedFilters,
            startDate: getState().appointments.filters.startDate,
            endDate: getState().appointments.filters.endDate,
        }));
        dispatch(setHiddenGroups({}));
        dispatch(filterAppointmentSlots());

        //console.log('apiResponse:', apiResponse);
    } catch (e) {
        console.log('Error getAppointmentSlots', e);
        dispatch(showExceptionSnackbar(e));
        dispatch(openSnackbar('error', e.message));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
    }
};
// const filter = `startDate gte ${new Date().toISOString()} and startDate lte ${moment().add(2, "days").toISOString()}`;

export const changeAppointmentStatusOnDischarge = (pt) => async (dispatch, getState) => {
    const ptId = pt?.patient?.hospitalNumber;
    const hospitalId = pt?.admission?.facility;

    const filterStr = `patientId=${ptId}${hospitalId ? `&hospitalId=${hospitalId}` : ""}`;

    try {
        const patientSlots = await API.getAppointmentSlots(
            moment(),
            getConfig('modules.scheduler.onlyTodayStatusChange', false) ? moment().add(1, 'day') : moment().add(3, "day"),
            false,
            filterStr,
        );

        if (_.isArray(patientSlots) && !_.isEmpty(patientSlots)) {
            const toUpdate = _.filter(patientSlots, slot => {
                return slot?.patientId === ptId && slot?.status !== 'notRegistered' && slot?.status !== 'departed'
            });

            if (_.isArray(toUpdate) && toUpdate.length) {
                if (toUpdate.length === 1) {
                    dispatch(updateAppointmentStatus(toUpdate.at(0)?.__id, "departed", null, null, null, pt?.__id));
                    if (getState()?.patientContext?.showDischargeTemplates) dispatch(setShowDischargeTemplates(false));
                } else {
                    dispatch(openSnackbar('warning', "The patient has more than one active appointment scheduled. Please manually change the appointment slot status to 'Departed'."));
                }
            }
        }
    } catch (err) {
        dispatch(openSnackbar('error', "Patient has more than one appointment scheduled on " ))
    }
}

export const updateAppointmentStatus =
    (slotId, status, onSuccess, onError, noteDate, ptInWardRef) => async (dispatch, getState) => {
        if (!isString(slotId)) return false;

        dispatch(setAgyleHeaderLoading(true));
        try {
            const currentSlot = await API.getAppointmentSlot(slotId, false);

            const response = await API.updateAppointmentSlotStatus(slotId, {
                status,
            });

            dispatch(sendUserAudit({patient: response?.patient}, AUDIT_TYPES.SCHEDULER_CHANGE_STATUS, {
                status,
                appointmentId: slotId,
            }));

            if (onSuccess && response) {
                onSuccess(response);
            }

            const oldVal = currentSlot ? currentSlot :  _.find(getState()?.appointments?.appointments, { __id: slotId });

            if (response) {
                let patientsInWardsRef = _.isString(ptInWardRef) ? [ptInWardRef] : [];

                if (oldVal?.patient) {
                    dispatch(addPatientSchedulerNote({
                        ptId: response.patientId,
                        oldValues: oldVal,
                        newValues: response,
                        patientsInWardsRef,
                        noteDate,
                        isDeleted: status === AppointmentSlotStatus.DELETED
                    }))
                }

                if (status === SLOT_STATUSES.DNA.value) {
                    const pt = await API.getPatientByID(currentSlot.patientId);
                    if (!pt) return;

                    const tags = pt?.tags || [];
                    const withoutBedas = _.filter(tags, t => t !== 'bedas');

                    await API.updatePatientTags(pt.hospitalNumber, withoutBedas).then((e) =>
                        console.log("Flag saved"),
                    );
                }
            }
            dispatch(getAppointmentSlots(true));
            return response;
        } catch (e) {
            if (onError) onError(e);
            dispatch(showExceptionSnackbar(e));
        } finally {
            dispatch(setAgyleHeaderLoading(false));
        }
    };

export const removePatientAppointment = (slotId, onSuccess, onError) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));
    try {
        const response = await API.removeAppointment(slotId);
        if (response && onSuccess) {
            onSuccess(response);
        }
        return response;
    } catch (e) {
        if (onError) onError(e);
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots());
    }
};


//////////////////////////
//  APPOINTMENT  SLOTS  //
//////////////////////////
export const createAppointmentSlot = (slot, remove, onSuccess, noAddNote = false, attendanceRef) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));
    const preparedSlot = {
        ...slot,
        startDate: moment(slot.startDate).toDate(),
        endDate: moment(slot.endDate).toDate(),
    };
    try {
        if (remove) {
            const response = await API.removeAppointment(remove);
            if (!response) {
                dispatch(showExceptionSnackbar({message: 'Cannot remove old appointment'}));
            } else if (!noAddNote) {
                const noteProps = {
                    ptId: apiResponse?.patientId,
                    newValues: response,
                    isDeleted: true,
                }
                if (attendanceRef) noteProps.patientsInWardsRef = attendanceRef;

                dispatch(addPatientSchedulerNote(noteProps));
            }
            return;
        }

        const apiResponse = await API.createAppointmentSlot(preparedSlot);

        if (apiResponse?.__id) {
            onSuccess(apiResponse);
            if (!noAddNote) {
                const noteProps = {
                    ptId: apiResponse?.patientId,
                    newValues: apiResponse,
                    isDeleted: false,
                }
                if (attendanceRef) noteProps.patientsInWardsRef = attendanceRef;

                dispatch(addPatientSchedulerNote(noteProps));
            }
        }
        dispatch(getAppointmentSlots(true)); //just to refresh data
        return apiResponse;
    } catch (e) {
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
    }
};

export const lockAppointmentSlot = (event, reason, onSuccess) => async (dispatch) => {
    dispatch(setAgyleHeaderLoading(true));

    const preparedLockedSlot = {
        assignedSession: event?.session?.sessionId,
        startDate: moment(event.slot.startDate).toDate(),
        endDate: moment(event.slot.endDate).toDate(),
        description: reason || '',
        wardId: event.session.wardId,
        hospitalId: event.session.hospitalId,
    };

    try {
        const apiResponse = await API.lockAppointmentSlot(preparedLockedSlot);

        if (apiResponse?.__id) onSuccess(apiResponse);
        return apiResponse;
    } catch (e) {
        dispatch(showExceptionSnackbar(e));
    } finally {
        dispatch(setAgyleHeaderLoading(false));
        dispatch(getAppointmentSlots());
    }
};

export default appointmentSlice.reducer;
